import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from 'src/environments/environment';
import { FormDataService } from 'src/app/shared/attachments/service/form-data.service';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { IdName } from 'src/app/shared/models/id-name';
import { ApiResponse } from 'src/app/core/models/api-response';
import { defaultInitialPageSize } from 'src/app/core/table-config/table-paging-defaults';
import {
  IApiResponseGroupTrips,
  IAvailableTrip,
  ICrewMemberUpdate,
  IPaxUpdate,
  ITripChangeLog,
  ITripCreate,
  ITripLog,
  ITripPagedListItem,
  ITripResponse,
} from '../models/trip.model';
import { ITripList } from '../models/trip-list.model';

@Injectable({
  providedIn: 'root',
})
export class TripApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return this.baseUrl(getDepartmentId());
  }

  private baseUrl(departmentId: string): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${departmentId}/trips`;
  }

  private baseUrlForTenant(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/trips`;
  }

  constructor(private http: HttpClient) {}

  getTrips(
    forTenant: boolean,
    currentFilter?: MainTableFilterModel,
    departmentId?: string
  ): Observable<IApiResponseGroupTrips> {
    let params = createFilterParams(currentFilter);

    if (typeof currentFilter?.additional?.status === 'number') {
      params = params.set(
        'SearchBy[Status]',
        JSON.stringify({ In: [currentFilter.additional.status] })
      );
    }
    params = MainTableFilteringHelperService.appendFilteringParams(params, currentFilter);

    return this.http.get<IApiResponseGroupTrips>(
      forTenant ? this.baseUrlForTenant() : this.baseUrl(departmentId || getDepartmentId()),
      {
        params,
      }
    );
  }

  getTripsList(): Observable<ITripList[]> {
    return this.http.get<ITripList[]>(`${this.url}/list`);
  }

  getTripsPagedList(
    searchPhrase?: string,
    page = 0
  ): Observable<ApiResponse<ITripPagedListItem[]>> {
    const url = `${this.url}/PagedList`;
    let params = new HttpParams();
    const minSearchLength = 3;

    if (searchPhrase && searchPhrase.length >= minSearchLength) {
      params = params
        .set('GlobalSearch', searchPhrase)
        .set('SearchBy[Name]', JSON.stringify({ Contains: searchPhrase }))
        .set('Page', '0')
        .set('PageSize', '10000');
    } else {
      params = params.set('Page', String(page)).set('PageSize', String(defaultInitialPageSize));
    }

    params = params.set('OrderBy[CustomDefaultSortOrder]', 'Ascending').set('OrderBy[From]', 'Descending');

    return this.http.get<ApiResponse<ITripPagedListItem[]>>(url, { params });
  }

  getAvailableTrips(): Observable<IAvailableTrip[]> {
    const url = `${this.url}/available`;
    return this.http.get<IAvailableTrip[]>(url);
  }

  getTripById(tripId: string, departmentId?: string): Observable<ITripResponse> {
    const url = `${this.baseUrl(departmentId || getDepartmentId())}/${tripId}`;
    return this.http.get<ITripResponse>(url);
  }

  createTrip(trip: ITripCreate): Observable<IdName> {
    const tripToCreate: any = {
      ...trip,
      status: trip.viewStatus,
    };
    delete tripToCreate.viewStatus;
    const formData = FormDataService.mapToFormData(tripToCreate);
    return this.http.post<IdName>(this.url, formData);
  }

  updateTripForTripBased(trip: ITripCreate): Observable<ITripResponse> {
    const tripToUpdate = {
      ...trip,
      status: (trip as ITripCreate).viewStatus,
    };
    const formData = FormDataService.mapToFormData(tripToUpdate);
    const url = `${this.url}/${trip.id}/TripBased`;
    return this.http.put<ITripResponse>(url, formData);
  }

  updateTripForActivityBased(trip: ITripCreate): Observable<ITripResponse> {
    const url = `${this.url}/${trip.id}/ActivityBased`;
    return this.http.put<ITripResponse>(url, trip, { headers: this.headers });
  }

  endTrip(tripId: string, endDate: Date): Observable<ITripResponse> {
    const url = `${this.url}/${tripId}/complete`;
    const body = {
      endTime: FormDataService.dateToFormDataString(endDate),
    };
    return this.http.put<ITripResponse>(url, body, { headers: this.headers });
  }

  deleteTrip(tripId: string) {
    const url = `${this.url}/${tripId}`;
    return this.http.delete<ITripResponse>(url);
  }

  printTripPdf(tripId: string, onlyCurrentlyOnBoard: boolean): Observable<Blob> {
    let params = new HttpParams();
    params = params.append('OnlyCurrentlyOnBoard', onlyCurrentlyOnBoard);
    const url = `${this.url}/${tripId}/print/pdf`;
    return this.http.get(url, { params, responseType: 'blob' });
  }

  getTripLog(tripId: string): Observable<ITripLog[]> {
    const url = `${this.url}/${tripId}/log`;
    return this.http.get<{ results: ITripLog[] }>(url).pipe(map((res) => res.results));
  }

  addCrewMembersToTrip(tripId: string, usersId: string[]): Observable<void> {
    const url = `${this.url}/${tripId}/crew`;
    return this.http.post<void>(url, { usersId });
  }

  updateCrewMember(
    tripId: string,
    crewMemberId: string,
    crewMember: ICrewMemberUpdate
  ): Observable<void> {
    const url = `${this.url}/${tripId}/crew/${crewMemberId}`;
    return this.http.put<void>(url, crewMember);
  }

  deleteCrewMemberFromTrip(tripId: string, userIds: string[]): Observable<void> {
    const url = `${this.url}/${tripId}/crew`;
    return this.http.delete<void>(url, { params: { userIds } });
  }

  addPaxesToTrip(tripId: string, paxesId: string[]): Observable<void> {
    const url = `${this.url}/${tripId}/pax`;
    return this.http.post<void>(url, { paxesId });
  }

  updatePax(tripId: string, paxId: string, pax: IPaxUpdate): Observable<void> {
    const url = `${this.url}/${tripId}/pax/${paxId}`;
    return this.http.put<void>(url, pax);
  }

  deletePaxFromTrip(tripId: string, paxIds: string[]): Observable<void> {
    const url = `${this.url}/${tripId}/pax`;
    return this.http.delete<void>(url, { params: { paxIds } });
  }

  getTripsChangeLog(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<ITripChangeLog[]>> {
    const url = `${this.url}/log`;
    let params = createFilterParams(currentFilter);
    params = MainTableFilteringHelperService.appendFilteringParams(params, currentFilter);

    return this.http.get<ApiResponse<ITripChangeLog[]>>(url, { params });
  }
}
